<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>Divisions</v-toolbar-title>
      <v-spacer></v-spacer>
      <div class="mr-5 pt-5">
        <v-switch label="Show All" v-model="showAll"></v-switch>
      </div>
      <v-btn
        @click.native.stop="dialog = true"
        color="primary"
        dark
        class="mb-2"
        >New Division</v-btn
      >
    </v-toolbar>
    <v-row>
      <v-col cols="12">
        <v-dialog v-model="dialog" max-width="550px">
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="editedItem.name"
                      label="Division Name"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-switch
                      v-model="editedItem.active"
                      label="Active"
                    ></v-switch>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editedItem.managerId"
                      label="Division Manager"
                      :items="userOptions"
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editedItem.teamIds"
                      label="Teams"
                      :items="teamOptions"
                      chips
                      multiple
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click.native="close"
                >Cancel</v-btn
              >
              <v-btn color="blue darken-1" text @click.native="save"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-data-table
          :footer-props="{ 'items-per-page-options': [-1] }"
          :headers="headers"
          hide-default-footer
          :items="divisions"
          :loading="$apollo.loading"
          :sort-by="['name']"
          :sort-desc="[false]"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.name }}</td>
                <td>{{ item.managerName }}</td>
                <td>{{ item.teamNames }}</td>
                <td>
                  <v-simple-checkbox
                    color="primary"
                    :value="item.active"
                    :ripple="false"
                  />
                </td>
                <td class="justify-center layout px-0">
                  <v-icon
                    small
                    class="mr-2"
                    color="teal"
                    @click="editItem(item)"
                    >mdi-account-edit</v-icon
                  >
                  <v-icon small color="pink" @click="deleteItem(item)"
                    >mdi-delete</v-icon
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import filter from "lodash/filter";

export default {
  data() {
    return {
      dialog: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Manager", value: "managerName" },
        { text: "Teams", value: "teamNames" },
        { text: "Active", value: "active" },
        { text: "Actions", align: "center", value: "name", sortable: false }
      ],
      editedIndex: -1,
      editedItem: {
        name: "",
        active: true,
        managerId: null,
        teamIds: []
      },
      defaultItem: {
        name: "",
        active: true,
        managerId: null,
        teamIds: []
      },
      userOptions: [],
      teamOptions: [],
      showAll: false
    };
  },
  computed: {
    divisions() {
      let divisions = get(this.organization, "divisions");
      if (divisions && divisions.length > 0) {
        let mapped = divisions.map(d => ({
          id: d.id,
          name: d.name,
          active: d.active,
          managerId: d.manager ? d.manager.id : null,
          managerName: d.manager
            ? `${d.manager.firstName} ${d.manager.lastName}`
            : "—",
          teamIds: (d.teams || []).map(t => t.id),
          teamNames:
            (d.teams || []).map(t => t.name).join(", ") || "No teams assigned"
        }));
        return this.showAll ? mapped : filter(mapped, d => d.active);
      }
      return [];
    },
    formTitle() {
      return this.editedIndex === -1 ? "New Division" : "Edit Division";
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    "organization.users"(val) {
      this.userOptions = sortBy(
        val.map(u => ({
          text: `${u.firstName} ${u.lastName}`,
          value: u.id
        })),
        [item => item.text]
      );
    },
    "organization.teams"(val) {
      this.teamOptions = sortBy(
        val.map(t => ({ text: t.name, value: t.id })),
        [item => item.text]
      );
    }
  },
  apollo: {
    organization: {
      query: gql`
        query($id: ID!) {
          organization(id: $id) {
            id
            name
            users {
              id
              firstName
              lastName
            }
            teams {
              id
              name
              active
            }
            divisions {
              id
              name
              active
              manager {
                id
                firstName
                lastName
              }
              teams {
                id
                name
              }
            }
          }
        }
      `,
      variables() {
        return { id: this.$route.params.id };
      }
    }
  },
  methods: {
    editItem(item) {
      this.editedIndex = this.divisions.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    deleteItem(item) {
      if (
        !confirm(`Are you sure you want to delete the ${item.name} division?`)
      )
        return;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($id: ID!) {
              deleteDivision(id: $id) {
                id
              }
            }
          `,
          variables: { id: item.id }
        })
        .then(() => {
          this.$apollo.queries.organization.refetch();
        })
        .catch(error => console.error(error));
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    save() {
      const division = {
        name: this.editedItem.name,
        active: this.editedItem.active,
        managerId: this.editedItem.managerId,
        teamIds: this.editedItem.teamIds
      };
      if (this.editedItem.id) {
        division.id = this.editedItem.id;
      }
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($division: UpsertDivisionInput!) {
              upsertDivision(division: $division) {
                id
                name
                active
              }
            }
          `,
          variables: { division }
        })
        .then(() => {
          this.close();
          this.$apollo.queries.organization.refetch();
        })
        .catch(error => console.error(error));
    }
  }
};
</script>

<style scoped></style>
